// File: frontend/src/components/marketing/visuals/PaymentPhoneScreens.js
//
// The customer's side of a purchase, drawn in CSS for the landing page's
// payment journey. The wording follows the real captive portal
// (backend/utils/portalRenderer.js): "Available Plans", "Enter Mobile Money
// Number", "Pay Now", "Payment Confirmed!", "You're connected", "Session
// time". Networks are named as text; no provider logos are used.
//
// The wallet card's amount is the 3 Hours price less PesaFii's published fees
// (8% of UGX 2,000 = UGX 160; content/publicPricing.js).
//
// Fictional data only.

import React from "react";
import { Box, Paper, Stack, Typography, useTheme, alpha } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import WifiIcon from "@mui/icons-material/Wifi";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { COLLECTION_FEE_TOTAL_PCT } from "../../../content/publicPricing";

const PRICE = 2000;
const NET = PRICE - Math.round((PRICE * COLLECTION_FEE_TOTAL_PCT) / 100);
const ugx = (n) => `UGX ${n.toLocaleString("en-US")}`;

function ScreenHeader({ subtitle }) {
  return (
    <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: "text.primary" }}>Central Café Wi-Fi</Typography>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{subtitle}</Typography>
    </Box>
  );
}

const Footer = () => (
  <Typography sx={{ mt: "auto", pt: 1.5, pb: 1.5, textAlign: "center", fontSize: 10.5, color: "text.disabled" }}>
    Powered by <Box component="span" sx={{ fontWeight: 800, color: "text.secondary" }}>PesaFii</Box>
  </Typography>
);

const PLANS = [
  { name: "1 Hour", price: 1000 },
  { name: "3 Hours", price: PRICE, selected: true },
  { name: "24 Hours", price: 5000 },
];

export function PlansScreen() {
  const theme = useTheme();
  const green = theme.palette.primary.main;
  return (
    <Stack sx={{ height: "100%" }}>
      <ScreenHeader subtitle="Fast internet. Simple login." />
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 1 }}>Available Plans</Typography>
        <Stack spacing={1}>
          {PLANS.map((p) => (
            <Stack key={p.name} direction="row" justifyContent="space-between" alignItems="center" sx={{
              px: 1.5, py: 1.25, borderRadius: 2, border: "1.5px solid",
              borderColor: p.selected ? green : "divider", bgcolor: p.selected ? alpha(green, 0.1) : "transparent",
            }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>{p.name}</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: p.selected ? "primary.main" : "text.secondary" }}>{ugx(p.price)}</Typography>
            </Stack>
          ))}
        </Stack>
        <Typography sx={{ mt: 2, fontSize: 11.5, color: "text.secondary", textAlign: "center" }}>Have a voucher? Voucher Login</Typography>
      </Box>
      <Footer />
    </Stack>
  );
}

export function PayScreen() {
  const theme = useTheme();
  const green = theme.palette.primary.main;
  return (
    <Stack sx={{ height: "100%" }}>
      <ScreenHeader subtitle="Pay for package" />
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ px: 1.5, py: 1, mb: 2, borderRadius: 2, bgcolor: "action.hover" }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "text.primary" }}>3 Hours</Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "primary.main" }}>{ugx(PRICE)}</Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 0.75 }}>Enter Mobile Money Number</Typography>
        <Box sx={{ px: 1.5, py: 1.1, borderRadius: 2, border: "1.5px solid", borderColor: green, fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", color: "text.primary", mb: 1.25 }}>
          07•• ••• •••
        </Box>
        <Stack direction="row" spacing={0.75} sx={{ mb: 2 }}>
          {["MTN Mobile Money", "Airtel Money"].map((n) => (
            <Box key={n} sx={{ flex: 1, textAlign: "center", py: 0.6, borderRadius: 1.5, fontSize: 10.5, fontWeight: 700, color: "text.secondary", border: "1px solid", borderColor: "divider" }}>{n}</Box>
          ))}
        </Stack>
        <Box sx={{ py: 1.1, borderRadius: 2, textAlign: "center", fontSize: 13.5, fontWeight: 800, bgcolor: "primary.main", color: "primary.contrastText" }}>Pay Now</Box>
        <Typography sx={{ mt: 1.25, fontSize: 11, color: "text.secondary", textAlign: "center" }}>
          Check your phone for a payment prompt
        </Typography>
      </Box>
      <Footer />
    </Stack>
  );
}

function BigIcon({ icon: Icon }) {
  const theme = useTheme();
  return (
    <Box sx={{ width: 72, height: 72, mx: "auto", mb: 2, borderRadius: "50%", display: "grid", placeItems: "center",
      bgcolor: alpha(theme.palette.primary.main, 0.16), color: "primary.main", boxShadow: `0 0 0 10px ${alpha(theme.palette.primary.main, 0.06)}` }}>
      <Icon sx={{ fontSize: 38 }} />
    </Box>
  );
}

export function ConfirmedScreen() {
  return (
    <Stack sx={{ height: "100%" }}>
      <ScreenHeader subtitle="Pay for package" />
      <Box sx={{ p: 2.5, textAlign: "center", my: "auto" }}>
        <BigIcon icon={CheckIcon} />
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary" }}>Payment Confirmed!</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>3 Hours · {ugx(PRICE)}</Typography>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 2 }}>Setting up your connection...</Typography>
      </Box>
      <Footer />
    </Stack>
  );
}

export function OnlineScreen() {
  return (
    <Stack sx={{ height: "100%" }}>
      <ScreenHeader subtitle="Your session is active." />
      <Box sx={{ p: 2.5, textAlign: "center", my: "auto" }}>
        <BigIcon icon={WifiIcon} />
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary" }}>You're connected</Typography>
        <Box sx={{ mt: 2, mx: "auto", maxWidth: 180, px: 1.5, py: 1, borderRadius: 2, bgcolor: "action.hover" }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Session time</Typography>
          <Typography sx={{ fontSize: 17, fontWeight: 800, color: "text.primary", fontVariantNumeric: "tabular-nums" }}>3:00:00</Typography>
        </Box>
      </Box>
      <Footer />
    </Stack>
  );
}

export function WalletEntryCard() {
  const theme = useTheme();
  const green = theme.palette.primary.main;
  return (
    <Paper sx={{ p: 1.75, borderRadius: 3, border: `1px solid ${alpha(green, 0.35)}`, width: 236,
      boxShadow: theme.palette.mode === "dark" ? "0 18px 40px rgba(0,0,0,0.5)" : "0 18px 40px rgba(15,23,42,0.16)" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        <AccountBalanceWalletIcon sx={{ fontSize: 17, color: green }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary" }}>Your wallet</Typography>
      </Stack>
      <Typography sx={{ fontSize: 20, fontWeight: 800, color: "success.main", lineHeight: 1.2 }}>+ {ugx(NET)}</Typography>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25 }}>
        3 Hours sale at Central Café, after the {COLLECTION_FEE_TOTAL_PCT}% fee
      </Typography>
    </Paper>
  );
}
